import { AddressDto, convertAddressDraftToDto } from "@modules/address/addressDto"
import { AddressDraft } from "@modules/address/addressTypes"
import { WARNING_OFFER_END_DELAY } from "@modules/boxSubscription/entities/boxSubscriptionConst"
import { BoxSubscriptionStatus } from "@modules/boxSubscription/gateway/boxSubscriptionApiDto"
import { word } from "@modules/core/utils/i18n"
import { convertImageFromDto } from "@modules/dtos/imageDto"
import { MemberDetailsFormikType } from "@modules/members/formik/memberFormik"
import {
  BoxMemberNextBookingDto,
  MemberActiveOfferDto,
  MemberDetailedDraftDto,
  MemberDetailedDto,
  MemberDetailedUpadteInput,
  MemberItemDto,
  MemberStatus,
} from "@modules/members/gateway/memberDto"
import { convertPublicProfileFromDto } from "@modules/profile/publicProfile/publicProfileDto"
import { PublicProfile } from "@modules/profile/publicProfile/publicProfileTypes"
import { HUImage } from "@modules/types/imageTypes"
import { defaultDateFormat } from "@modules/utils/dateUtils"
import { convertToEnum } from "@modules/utils/enum"
import dayjs from "dayjs"
import { DefaultTheme } from "styled-components"

export type MemberDetailedState = Omit<
  MemberDetailedEntity,
  "getInputUpdate" | "getFormikDraft" | "setNote" | "convertNoShow"
>

export type MemberItemState = Omit<MemberItemEntity, "getUserName">

export class MemberItemEntity {
  id: string
  avatar?: HUImage
  firstname: string
  lastname: string
  username: string
  status: MemberStatus
  note?: string
  lastVisited?: Date
  noShowPastMonth?: number
  activeOffers: ActiveOfferEntity[]

  private constructor(memberItem: MemberItemState) {
    this.id = memberItem.id
    this.avatar = memberItem.avatar
    this.firstname = memberItem.firstname
    this.lastname = memberItem.lastname
    this.username = memberItem.username
    this.status = memberItem.status
    this.lastVisited = memberItem.lastVisited
    this.noShowPastMonth = memberItem.noShowPastMonth
    this.note = memberItem.note
    this.activeOffers = memberItem.activeOffers
  }

  static fromItemDto(dto: MemberItemDto): MemberItemEntity {
    return new MemberItemEntity({
      ...dto,
      avatar: dto.avatar ? convertImageFromDto(dto.avatar) : undefined,
      lastVisited: dto.lastVisited ? new Date(dto.lastVisited) : undefined,
      noShowPastMonth: dto.noShowPastMonth ? dto.noShowPastMonth : 0,
      note: dto.note ? dto.note : "",
      activeOffers: dto.activeOffers.map((offer) => ActiveOfferEntity.fromDto(offer)),
    })
  }

  getUserName(): string {
    if (this.username === "") {
      return this.username
    }
    return "@" + this.username
  }
}

export class MemberDetailedEntity {
  id: string
  userId: string
  firstname: string
  lastname: string
  username: string
  inscriptionDate?: Date
  lastVisited?: Date
  birthdate?: Date
  phoneNumber: string
  address?: AddressDto
  email: string
  emergencyInformation: string
  status: MemberStatus
  note?: string
  noShowPastMonth?: number
  activeOffersTitles?: string[]
  nextBookings: BoxMemberNextBooking[]
  publicProfile?: PublicProfile

  private constructor(memberDetailed: MemberDetailedState) {
    this.id = memberDetailed.id
    this.userId = memberDetailed.userId
    this.firstname = memberDetailed.firstname
    this.lastname = memberDetailed.lastname
    this.inscriptionDate = memberDetailed.inscriptionDate
    this.username = memberDetailed.username
    this.birthdate = memberDetailed.birthdate
    this.phoneNumber = memberDetailed.phoneNumber
    this.email = memberDetailed.email
    this.address = memberDetailed.address
    this.emergencyInformation = memberDetailed.emergencyInformation ?? ""
    this.note = memberDetailed.note
    this.status = memberDetailed.status
    this.lastVisited = memberDetailed.lastVisited
    this.noShowPastMonth = memberDetailed.noShowPastMonth
    this.activeOffersTitles = memberDetailed.activeOffersTitles
    this.nextBookings = memberDetailed.nextBookings
    this.publicProfile = memberDetailed.publicProfile
  }

  static fromDto(dto: MemberDetailedDto): MemberDetailedEntity {
    return new MemberDetailedEntity({
      ...dto,
      birthdate: dto.birthDate ? new Date(dto.birthDate) : undefined,
      phoneNumber: dto.phone,
      status: convertToEnum(MemberStatus, dto.status, MemberStatus.TERMINATED),
      inscriptionDate: dto.inscriptionDate ? new Date(dto.inscriptionDate) : undefined,
      lastVisited: dto.lastVisited ? new Date(dto.lastVisited) : undefined,
      publicProfile: dto.publicProfile ? convertPublicProfileFromDto(dto.publicProfile) : undefined,
      nextBookings: dto.nextBookings.map((nextBooking) => BoxMemberNextBooking.fromDto(nextBooking)),
      noShowPastMonth: dto.noShowPastMonth || 0,
    })
  }

  static getDraftforFormik(): MemberDetailsFormikType {
    return {
      firstname: "",
      lastname: "",
      username: "",
      birthDate: undefined,
      phoneNumber: "",
      email: "",
      emergencyInformation: "",
      note: "",
      subscriptionId: "",
      address: "",
      additionalAddress: "",
      postalCode: "",
      city: "",
      country: "",
    }
  }

  convertNoShow = () => {
    if (!this.noShowPastMonth) return 0

    if (this.noShowPastMonth > 3) {
      return 1
    } else if (this.noShowPastMonth >= 1 && this.noShowPastMonth <= 2) {
      return 2
    } else {
      return 3
    }
  }

  setNote(note: string): MemberDetailedEntity {
    return new MemberDetailedEntity({
      ...this,
      note,
    })
  }

  getInputUpdate(): MemberDetailedUpadteInput {
    return {
      firstname: this.firstname,
      lastname: this.lastname,
      birthDate: this.birthdate ? dayjs(this.birthdate).format(defaultDateFormat) : undefined,
      phone: this.phoneNumber,
      address: this.address ?? undefined,
      emergencyInformation: this.emergencyInformation ?? "",
      note: this.note || "",
    }
  }
}

export class MemberDetailedDraftEntity {
  firstname: string
  lastname: string
  username?: string
  birthDate?: Date
  phoneNumber: string
  email: string
  address: AddressDraft
  emergencyInformation: string
  note: string
  activeOffersTitles?: string[]

  private constructor(memberDetailedDraft: MemberDetailedDraftEntity) {
    this.firstname = memberDetailedDraft.firstname
    this.lastname = memberDetailedDraft.lastname
    this.username = memberDetailedDraft.username
    this.birthDate = memberDetailedDraft.birthDate
    this.phoneNumber = memberDetailedDraft.phoneNumber
    this.email = memberDetailedDraft.email
    this.address = memberDetailedDraft.address
    this.emergencyInformation = memberDetailedDraft.emergencyInformation
    this.note = memberDetailedDraft.note
    this.activeOffersTitles = memberDetailedDraft.activeOffersTitles
  }

  static toDto(draft: MemberDetailedDraftEntity): MemberDetailedDraftDto {
    return {
      email: draft.email,
      firstname: draft.firstname,
      lastname: draft.lastname,
      username: draft.username?.trim() || undefined,
      birthDate: draft.birthDate ? dayjs(draft.birthDate).format(defaultDateFormat) : undefined,
      phone: draft.phoneNumber,
      address: convertAddressDraftToDto(draft.address),
      emergencyInformation: draft.emergencyInformation,
      note: draft.note,
    }
  }
}

export class BoxMemberNextBooking {
  slotId: string
  name: string
  date: Date
  color: string

  private constructor(boxMemberNextBooking: BoxMemberNextBooking) {
    this.slotId = boxMemberNextBooking.slotId
    this.name = boxMemberNextBooking.name
    this.date = boxMemberNextBooking.date
    this.color = boxMemberNextBooking.color
  }

  static fromDto(dto: BoxMemberNextBookingDto): BoxMemberNextBooking {
    return new BoxMemberNextBooking({
      ...dto,
      date: new Date(dto.date),
    })
  }
}

export type ActiveOfferState = Omit<ActiveOfferEntity, "getWarning">
export class ActiveOfferEntity {
  offerName: string
  endValidity: Date | null
  endSubscription: Date | null
  subscriptionStatus: BoxSubscriptionStatus | null

  private constructor(activeOffer: ActiveOfferState) {
    this.offerName = activeOffer.offerName
    this.endValidity = activeOffer.endValidity
    this.endSubscription = activeOffer.endSubscription
    this.subscriptionStatus = activeOffer.subscriptionStatus
  }

  static fromDto(dto: MemberActiveOfferDto): ActiveOfferEntity {
    return new ActiveOfferEntity({
      ...dto,
      endValidity: dto.endValidity ? new Date(dto.endValidity) : null,
      endSubscription: dto.endSubscription ? new Date(dto.endSubscription) : null,
      subscriptionStatus: dto.subscriptionStatus ?? null,
    })
  }

  getWarning(theme: DefaultTheme): { warningColor: string; warningContent: string } | undefined {
    if (
      this.subscriptionStatus === BoxSubscriptionStatus.PENDING_CANCELLATION &&
      this.endSubscription &&
      dayjs(this.endSubscription).diff(dayjs(), "day") <= WARNING_OFFER_END_DELAY
    )
      return {
        warningColor: theme.textWarning,
        warningContent: word("box.member.table.offer.item.status.ends", {
          count: dayjs(this.endSubscription).startOf("day").diff(dayjs().startOf("day"), "day"),
        }),
      }
    else if (this.subscriptionStatus === BoxSubscriptionStatus.PAYMENT_IN_ERROR)
      return {
        warningColor: theme.textWarning,
        warningContent: word("box.members.tab.offer.status.paymentError"),
      }
  }
}
